import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, RefreshCcw, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import Pagination from '../../components/Pagination';
import StatusBadge from '../../components/StatusBadge';
import Alert from '../../components/alert';
import SimpleDropdown from '../../components/SimpleDropdown';
import Modal from '../../components/modal';
import RowCheckbox from '../../components/RowCheckbox';
import ImageWithFallback from '../../components/ImageWithFallback';
import {
	listProducts,
	getStorePaceAccount,
	triggerStoreProductSync,
	getStoreProductSyncStatus,
	publishErpProduct,
	updateProduct,
	listCategories,
} from '../../utils/queries';

const PAGE_SIZE = 20;

const BULK_STATUS_OPTIONS = [
	{ value: '', label: 'No change' },
	{ value: 'active', label: 'Active' },
	{ value: 'inactive', label: 'Inactive' },
];

const STATUS_DISPLAY = {
	published: { status: 'active', text: 'Active' },
	active: { status: 'active', text: 'Active' },
	inactive: { status: 'inactive', text: 'Inactive' },
	discontinued: { status: 'inactive', text: 'Discontinued' },
	unpublished: { status: 'pending', text: 'Inactive' },
};

const STATUS_FILTER_OPTIONS = [
	{ value: '', label: 'All Statuses' },
	{ value: 'active', label: 'Active' },
	{ value: 'inactive', label: 'Inactive' },
	{ value: 'discontinued', label: 'Discontinued' },
	{ value: 'unpublished', label: 'Not Published' },
];

export default function ProductsList() {
	const { activeStoreId } = useContext(MyContext);
	const navigate = useNavigate();
	const [products, setProducts] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('active');
	const [hasPaceAccount, setHasPaceAccount] = useState(false);
	const [lastSyncedAt, setLastSyncedAt] = useState(null);
	const [syncing, setSyncing] = useState(false);
	const [syncError, setSyncError] = useState('');
	const [syncNotice, setSyncNotice] = useState('');
	const [actioningKey, setActioningKey] = useState(null);
	const [actionError, setActionError] = useState('');
	const [selectedIds, setSelectedIds] = useState(new Set());
	const [categories, setCategories] = useState([]);
	const [bulkModalOpen, setBulkModalOpen] = useState(false);
	const [bulkStatus, setBulkStatus] = useState('');
	const [bulkCategoryId, setBulkCategoryId] = useState('');
	const [bulkSaving, setBulkSaving] = useState(false);
	const [bulkError, setBulkError] = useState('');

	const refresh = () => {
		setLoading(true);
		listProducts({
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
			search: search || undefined,
			status: statusFilter || undefined,
			storeId: activeStoreId || undefined,
			includeErp: true,
		}).then((result) => {
			if (result?.success) {
				setProducts(result.data.products);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, search, statusFilter, activeStoreId]);

	const refreshSyncStatus = () => {
		if (!activeStoreId) {
			setLastSyncedAt(null);
			return;
		}
		getStoreProductSyncStatus(activeStoreId).then((result) => {
			setLastSyncedAt(result?.success ? result.data?.lastSyncedAt || null : null);
		});
	};

	useEffect(() => {
		setSyncError('');
		if (!activeStoreId) {
			setHasPaceAccount(false);
			setLastSyncedAt(null);
			return;
		}
		getStorePaceAccount(activeStoreId).then((result) => {
			setHasPaceAccount(Boolean(result?.success && result.data));
		});
		refreshSyncStatus();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStoreId]);

	useEffect(() => {
		setSelectedIds(new Set());
	}, [search, statusFilter, activeStoreId]);

	useEffect(() => {
		if (!activeStoreId) {
			setCategories([]);
			return;
		}
		listCategories({ storeId: activeStoreId, limit: 100 }).then((result) => {
			if (result?.success) setCategories(result.data.categories);
		});
	}, [activeStoreId]);

	const selectableIds = products.filter((p) => p.productId).map((p) => p.productId);
	const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
	const someSelected = !allSelected && selectableIds.some((id) => selectedIds.has(id));

	const toggleSelectAll = () => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (allSelected) {
				selectableIds.forEach((id) => next.delete(id));
			} else {
				selectableIds.forEach((id) => next.add(id));
			}
			return next;
		});
	};

	const toggleRow = (productId) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(productId)) next.delete(productId);
			else next.add(productId);
			return next;
		});
	};

	const openBulkModal = () => {
		setBulkError('');
		setBulkStatus('');
		setBulkCategoryId('');
		setBulkModalOpen(true);
	};

	const handleBulkAssign = async () => {
		const fields = {};
		if (bulkStatus) fields.status = bulkStatus;
		if (bulkCategoryId) fields.categoryId = bulkCategoryId === 'none' ? null : Number(bulkCategoryId);
		if (Object.keys(fields).length === 0) return;

		setBulkSaving(true);
		setBulkError('');
		const ids = Array.from(selectedIds);
		const results = await Promise.all(ids.map((id) => updateProduct(id, fields)));
		setBulkSaving(false);

		const failedCount = results.filter((r) => !r?.success).length;
		if (failedCount > 0) {
			setBulkError(`Failed to update ${failedCount} of ${ids.length} product(s).`);
			return;
		}
		setBulkModalOpen(false);
		setSelectedIds(new Set());
		refresh();
	};

	const handleSync = async () => {
		setSyncing(true);
		setSyncError('');
		setSyncNotice('');
		const result = await triggerStoreProductSync(activeStoreId);
		setSyncing(false);
		if (!result?.success) {
			setSyncError(result?.error?.message || 'Failed to sync products from ERP.');
			return;
		}
		// The API responds 200 even when the sync itself failed for this store
		// (e.g. no store_pace_accounts row yet) -- the real outcome is nested
		// in data.result, so check it rather than trusting the HTTP success.
		const syncErrors = result.data?.result?.errors || [];
		if (syncErrors.length > 0) {
			setSyncError(syncErrors.map((e) => e.message).join(' '));
			return;
		}
		const { upserted = 0, pending = 0, alerted = 0, discontinuedItems = [] } = result.data?.result || {};
		const parts = [
			`${upserted} product${upserted === 1 ? '' : 's'} updated`,
			`${pending} pending review`,
		];
		if (alerted > 0) parts.push(`${alerted} newly flagged for review`);
		if (discontinuedItems.length > 0) {
			parts.push(
				`${discontinuedItems.length} discontinued — no longer found in ERP: ${discontinuedItems.map((item) => item.name).join(', ')}`,
			);
		}
		setSyncNotice(`Synced with ERP — ${parts.join(', ')}.`);
		refresh();
		refreshSyncStatus();
	};

	const handlePublish = async (row) => {
		setActionError('');
		setActioningKey(`erp-${row.erpProductId}`);
		const result = await publishErpProduct(row.erpProductId);
		setActioningKey(null);
		if (!result?.success) {
			setActionError(result?.error?.message || 'Failed to publish product.');
			return;
		}
		navigate(`/products/${result.data.productId}/edit`);
	};

	const handleInactivate = async (row) => {
		setActionError('');
		setActioningKey(`product-${row.productId}`);
		const result = await updateProduct(row.productId, { status: 'inactive' });
		setActioningKey(null);
		if (!result?.success) {
			setActionError(result?.error?.message || 'Failed to inactivate product.');
			return;
		}
		refresh();
	};

	const handleReactivate = async (row) => {
		setActionError('');
		setActioningKey(`product-${row.productId}`);
		const result = await updateProduct(row.productId, { status: 'active' });
		setActioningKey(null);
		if (!result?.success) {
			setActionError(result?.error?.message || 'Failed to reactivate product.');
			return;
		}
		refresh();
	};

	const columns = [
		{
			key: '__select',
			header: (
				<RowCheckbox checked={allSelected} indeterminate={someSelected} onChange={toggleSelectAll} />
			),
			sortable: false,
			render: (_value, row) =>
				row.productId ? (
					<RowCheckbox
						checked={selectedIds.has(row.productId)}
						onChange={() => toggleRow(row.productId)}
						className={selectedIds.has(row.productId) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
					/>
				) : null,
		},
		{
			key: 'name',
			header: 'Product',
			render: (value, row) => (
				<div className='flex items-center gap-3'>
					<div className='size-10 shrink-0 rounded-md bg-gray-100 overflow-hidden'>
						<ImageWithFallback src={row.imageUrl} alt={value} className='size-full object-cover border border-gray-200' fallbackClassName='size-full' />
					</div>
					<div className='min-w-0'>
						<div className='flex items-center gap-1.5'>
							{row.productId ? (
								<Link
									to={`/products/${row.productId}/edit`}
									className='text-black font-medium hover:underline truncate'
								>
									{value}
								</Link>
							) : (
								<span className='text-black font-medium truncate'>{value}</span>
							)}
							{row.paceMissingSince && (
								<AlertCircle
									className='size-3.5 text-pi-red shrink-0'
									data-tooltip-id='product-actions-tooltip'
									data-tooltip-content={`Not found in ERP ${new Date(row.paceMissingSince).toLocaleDateString()}`}
									aria-label='Not found in ERP'
								/>
							)}
						</div>
						{row.categoryName && <p className='text-xs text-gray-400 truncate'>{row.categoryName}</p>}
					</div>
				</div>
			),
		},
		{
			key: 'productNumber',
			header: 'Product #',
			render: (value, row) => value || row.erpProductNumber,
		},
		{
			key: 'status',
			header: 'Status',
			render: (value) => {
				const display = STATUS_DISPLAY[value] || { status: value, text: value };
				return <StatusBadge status={display.status} text={display.text} />;
			},
		},
		{ key: 'featuredFlag', header: 'Featured', render: (value) => (value ? 'Yes' : '') },
		{
			key: 'actions',
			header: '',
			sortable: false,
			render: (_value, row) => {
				if (row.productId) {
					const key = `product-${row.productId}`;
					const isActive = row.status === 'published' || row.status === 'active';
					return (
						<button
							type='button'
							className='inline-flex items-center'
							disabled={actioningKey === key}
							onClick={() => (isActive ? handleInactivate(row) : handleReactivate(row))}
							data-tooltip-id='product-actions-tooltip'
							data-tooltip-content={isActive ? 'Inactivate product' : 'Activate product'}
						>
							{isActive ? <Eye size={20} /> : <Eye size={20} />}
						</button>
					);
				}
				const key = `erp-${row.erpProductId}`;
				return (
					<button
						type='button'
						className='inline-flex items-center'
						disabled={actioningKey === key}
						onClick={() => handlePublish(row)}
						data-tooltip-id='product-actions-tooltip'
						data-tooltip-content='Activate product'
					>
						<EyeOff size={20} />
					</button>
				);
			},
		},
	];

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-6'>
				<h1 className='text-2xl font-light tracking-tight'>All Products</h1>
				<div className='flex flex-col items-end gap-1'>
					{hasPaceAccount ? (
						<button type='button' className='flex items-center gap-2 btn-secondary' disabled={syncing} onClick={handleSync}>
							<RefreshCcw className={`size-4 ${syncing ? 'animate-spin' : ''}`} />
							{syncing ? 'Syncing…' : 'Sync'}
						</button>
					) : (
						<button type='button' className='btn-primary' onClick={() => navigate('/products/new')}>
							New Product
						</button>
					)}
					{hasPaceAccount && lastSyncedAt && (
						<span className='text-xxs text-gray-400'>
							Last synced {new Date(lastSyncedAt).toLocaleString()}
						</span>
					)}
				</div>
			</div>

			<div className='mb-5 flex items-end gap-4'>
				<div>
					<label>Search</label>
					<div className='relative max-w-sm'>
						<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
						<input
							className='w-52 form-input pl-9'
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setPage(0);
							}}
						/>
					</div>
				</div>
				<div>
					<label>Status</label>
					<SimpleDropdown
						className='w-48'
						value={statusFilter}
						onChange={(v) => {
							setStatusFilter(v);
							setPage(0);
						}}
						options={STATUS_FILTER_OPTIONS}
					/>
				</div>
			</div>

			{syncError && (
				<div className='mb-5'>
					<Alert alert={{ type: 'error', message: syncError }} />
				</div>
			)}

			{syncNotice && (
				<div className='mb-5'>
					<Alert alert={{ type: 'info', message: syncNotice }} />
				</div>
			)}

			{!loading && total === 0 && hasPaceAccount && !syncError && (
				<div className='mb-5'>
					<Alert
						alert={{
							type: 'info',
							message: 'No products yet - sync with ERP to pull the current catalog to get started.',
						}}
					/>
				</div>
			)}

			{actionError && <div className='text-sm text-pi-red mb-4'>{actionError}</div>}

			{selectedIds.size > 0 && (
				<div className='mb-5 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-md px-4 py-2'>
					<span className='text-sm text-gray-600'>
						{selectedIds.size} product{selectedIds.size === 1 ? '' : 's'} selected
					</span>
					<button type='button' className='btn-secondary-small' onClick={openBulkModal}>
						Edit {selectedIds.size} product{selectedIds.size === 1 ? '' : 's'}
					</button>
					<button
						type='button'
						className='text-sm text-gray-500 hover:text-black'
						onClick={() => setSelectedIds(new Set())}
					>
						Clear selection
					</button>
				</div>
			)}

			<p className='text-xs text-gray-400 text-left mb-2'>
				{total} product{total === 1 ? '' : 's'} found
			</p>

			<DataTable
				columns={columns}
				data={products}
				loading={loading}
				emptyHeading='No products found'
				rowClassName={() => 'group'}
			/>
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />

			<Modal open={bulkModalOpen} setOpen={setBulkModalOpen} title='Bulk Edit'>
				<p className='text-sm text-gray-500 mt-6'>
					Apply changes to {selectedIds.size} selected product{selectedIds.size === 1 ? '' : 's'}
				</p>

				{bulkError && <div className='text-sm text-pi-red py-2'>{bulkError}</div>}

				<div className='grid grid-cols-2 gap-4 mb-4'>
					<div className='py-2'>
						<label>Status</label>
						<SimpleDropdown value={bulkStatus} onChange={setBulkStatus} options={BULK_STATUS_OPTIONS} />
					</div>
					<div className='py-2'>
						<label>Category</label>
						<SimpleDropdown
							value={bulkCategoryId}
							onChange={setBulkCategoryId}
							options={[
								{ value: '', label: 'No change' },
								{ value: 'none', label: 'None' },
								...categories.map((c) => ({ value: String(c.categoryId), label: c.name })),
							]}
						/>
					</div>
				</div>

				<button
					type='button'
					className='btn-primary w-full'
					disabled={bulkSaving || (!bulkStatus && !bulkCategoryId)}
					onClick={handleBulkAssign}
				>
					{bulkSaving ? 'Assigning...' : 'Assign'}
				</button>
			</Modal>

			<ReactTooltip id='product-actions-tooltip' />
		</div>
	);
}
